import { useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, Search, X } from "lucide-react";
import {
  NAV_GROUP_ICONS,
  NAV_GROUPS,
  NAV_ITEMS,
  navItemForPath,
  type NavGroup,
  type NavItem,
} from "./nav-items";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { usePermissions } from "@/hooks/use-permissions";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const OPEN_GROUPS_KEY = "carselona-admin-sidebar-groups";

/**
 * Up to this many screens the whole menu fits on a laptop, so nothing folds: an
 * admin who has six screens should not have to open a section to reach them.
 */
const FLAT_MENU_LIMIT = 12;

function readOpenGroups(): NavGroup[] {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) ?? "null");
    if (Array.isArray(stored)) {
      return NAV_GROUPS.filter((group) => stored.includes(group));
    }
  } catch {
    // Unreadable or blocked: fall through to the default.
  }
  return ["Work"];
}

function writeOpenGroups(groups: NavGroup[]) {
  try {
    localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(groups));
  } catch {
    // Preference just won't persist.
  }
}

/** Every word typed must appear in the screen's name or its group: "tulsi miss". */
function matches(item: NavItem, words: string[]): boolean {
  const text = `${item.label} ${item.group ?? ""}`.toLowerCase();
  return words.every((word) => text.includes(word));
}

interface SidebarNavProps {
  /** Icon-rail mode: labels hidden, tooltips supply the name. */
  collapsed?: boolean;
  /** Called after navigating — closes the mobile drawer. */
  onNavigate?: () => void;
}

export function SidebarNav({ collapsed = false, onNavigate }: SidebarNavProps) {
  const { can } = usePermissions();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [openGroups, setOpenGroups] = useState(readOpenGroups);
  // The page on which its own section was folded by hand. Without this the
  // section holding the current page could not be folded at all.
  const [foldedOn, setFoldedOn] = useState<string | null>(null);

  /*
    Items the admin may not open are filtered out rather than disabled.

    A greyed-out link still tells a Team Leader there is a screen they cannot
    have, and invites the conversation; absent is quieter and truer to how the
    rest of this sidebar works, where an item appears when the screen exists
    for you.

    While permissions load, `can` says no, so gated items appear a moment after
    the Dashboard rather than appearing and then vanishing. A section whose
    items are all hidden is hidden with them — an empty heading is a dead end.
  */
  const visible = useMemo(
    () =>
      NAV_ITEMS.filter((item) => item.permission === undefined || can(item.permission)),
    [can],
  );
  const folds = visible.length > FLAT_MENU_LIMIT;
  const words = useMemo(
    () => query.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [query],
  );
  const searching = folds && !collapsed && words.length > 0;

  const sections = useMemo(() => {
    const shown = searching ? visible.filter((item) => matches(item, words)) : visible;
    return [
      { group: null, items: shown.filter((item) => item.group === undefined) },
      ...NAV_GROUPS.map((group) => ({
        group,
        items: shown.filter((item) => item.group === group),
      })),
    ].filter((section) => section.items.length > 0);
  }, [visible, searching, words]);

  const current = navItemForPath(pathname);

  // The section holding the current page opens by itself, but only for as long
  // as the admin is on it: remembering every section they pass through would
  // grow the menu back to its full length within a day. What they open by hand
  // stays open, in this browser.
  const isOpen = (group: NavGroup) =>
    group === current?.group ? foldedOn !== pathname : openGroups.includes(group);

  const toggle = (group: NavGroup) => {
    const open = isOpen(group);
    if (group === current?.group) setFoldedOn(open ? pathname : null);
    const next = open
      ? openGroups.filter((other) => other !== group)
      : [...openGroups, group];
    setOpenGroups(next);
    writeOpenGroups(next);
  };

  const afterNavigate = () => {
    setQuery("");
    onNavigate?.();
  };

  const onFindKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      const first = sections[0]?.items[0];
      if (!searching || first === undefined) return;
      event.preventDefault();
      void navigate(first.to);
      afterNavigate();
    } else if (event.key === "Escape" && query) {
      setQuery("");
    }
  };

  return (
    <nav aria-label="Main" className={cn("flex flex-col", folds ? "gap-1" : "gap-4")}>
      {folds && !collapsed ? (
        <div className="relative mb-2">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="text"
            aria-label="Find a screen"
            placeholder="Find a screen"
            autoComplete="off"
            spellCheck={false}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onFindKeyDown}
            className="h-8 border-border bg-sidebar-active/40 pl-8 pr-8"
          />
          {query ? (
            <button
              type="button"
              aria-label="Clear the search"
              onClick={() => setQuery("")}
              className="absolute right-1 top-1/2 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      ) : null}

      {searching && sections.length === 0 ? (
        <div className="space-y-2 px-2 py-1">
          <p className="break-words text-sm text-muted-foreground">
            No screens match “{query.trim()}”.
          </p>
          <Button variant="outline" size="sm" onClick={() => setQuery("")}>
            Clear search
          </Button>
        </div>
      ) : null}

      {sections.map(({ group, items }) => {
        const links = items.map((item) => (
          <SidebarLink
            key={item.to}
            item={item}
            collapsed={collapsed}
            onNavigate={afterNavigate}
          />
        ));

        if (group === null) {
          return (
            <div key="top" className={cn("flex flex-col gap-0.5", folds && "mb-2")}>
              {links}
            </div>
          );
        }

        if (collapsed && folds) {
          return (
            <RailGroup
              key={group}
              group={group}
              items={items}
              current={current}
              onNavigate={onNavigate}
            />
          );
        }

        if (folds && !searching) {
          const open = isOpen(group);
          const panelId = `sidebar-group-${group.toLowerCase().replace(/\s+/g, "-")}`;
          return (
            <div key={group} className={cn("flex flex-col gap-0.5", open && "pb-2")}>
              <GroupToggle
                group={group}
                open={open}
                holdsCurrent={group === current?.group}
                panelId={panelId}
                onToggle={() => toggle(group)}
              />
              {open ? (
                <div id={panelId} className="flex flex-col gap-0.5">
                  {links}
                </div>
              ) : null}
            </div>
          );
        }

        return (
          <div key={group} className="flex flex-col gap-0.5">
            {collapsed ? (
              // The rail has no room for a heading; a rule keeps the sections
              // apart without one.
              <div className="mx-2 mb-1 border-t border-border" aria-hidden="true" />
            ) : (
              <h2 className="px-2 pb-1 text-xs font-medium text-muted-foreground">
                {group}
              </h2>
            )}
            {links}
          </div>
        );
      })}
    </nav>
  );
}

function GroupToggle({
  group,
  open,
  holdsCurrent,
  panelId,
  onToggle,
}: {
  group: NavGroup;
  open: boolean;
  holdsCurrent: boolean;
  panelId: string;
  onToggle: () => void;
}) {
  // Folded over the current page, the highlighted link is out of sight, so the
  // heading says where you are instead: brighter, with a dot.
  const hidesCurrent = holdsCurrent && !open;
  return (
    <h2>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={onToggle}
        className={cn(
          "flex h-8 w-full cursor-pointer items-center gap-2 rounded-md px-2 text-xs font-medium transition-colors touch:h-11",
          "hover:bg-sidebar-active/60 hover:text-sidebar-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          hidesCurrent ? "text-sidebar-foreground" : "text-muted-foreground",
        )}
      >
        <span className="truncate">{group}</span>
        {hidesCurrent ? (
          <span className="size-1.5 shrink-0 rounded-full bg-primary">
            <span className="sr-only"> (current page is in here)</span>
          </span>
        ) : null}
        <ChevronRight
          className={cn(
            "ml-auto size-3.5 shrink-0 transition-transform duration-150",
            open && "rotate-90",
          )}
          aria-hidden="true"
        />
      </button>
    </h2>
  );
}

/**
 * A group in the icon rail: one button, its screens in a menu beside it. Thirty
 * unlabelled icons in a column cannot be told apart, and do not fit a laptop.
 */
function RailGroup({
  group,
  items,
  current,
  onNavigate,
}: {
  group: NavGroup;
  items: NavItem[];
  current: NavItem | undefined;
  onNavigate: (() => void) | undefined;
}) {
  const Icon = NAV_GROUP_ICONS[group];
  const holdsCurrent = group === current?.group;
  // Choosing a screen moves focus to the new page (usePageArrival), which the
  // menu handing focus back to its button would undo.
  const leavingPage = useRef(false);

  return (
    <DropdownMenu>
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger
            aria-label={group}
            className={cn(
              "flex h-8 w-full cursor-pointer items-center justify-center rounded-md transition-colors touch:h-11",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              holdsCurrent
                ? "bg-sidebar-active text-sidebar-foreground"
                : "text-muted-foreground hover:bg-sidebar-active/60 hover:text-sidebar-foreground",
            )}
          >
            <Icon
              className={cn("size-4 shrink-0", holdsCurrent && "text-primary")}
              aria-hidden="true"
            />
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">{group}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        side="right"
        align="start"
        sideOffset={12}
        onCloseAutoFocus={(event) => {
          if (leavingPage.current) event.preventDefault();
          leavingPage.current = false;
        }}
      >
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          {group}
        </DropdownMenuLabel>
        {items.map((item) => {
          const ItemIcon = item.icon;
          const isCurrent = item === current;
          return (
            <DropdownMenuItem
              key={item.to}
              asChild
              onSelect={() => {
                leavingPage.current = !isCurrent;
                onNavigate?.();
              }}
              className={cn("cursor-pointer", isCurrent && "bg-secondary font-medium")}
            >
              <Link to={item.to} aria-current={isCurrent ? "page" : undefined}>
                <ItemIcon
                  className={cn(isCurrent && "text-primary")}
                  aria-hidden="true"
                />
                {item.label}
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SidebarLink({
  item: { label, to, icon: Icon, end = false },
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate: (() => void) | undefined;
}) {
  const link = (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex h-8 items-center gap-2.5 rounded-md px-2 text-sm transition-colors touch:h-11",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          collapsed && "justify-center px-0",
          // Active is carried by the fill and the weight as well as the icon's
          // colour, so it never rests on colour alone.
          isActive
            ? "bg-sidebar-active font-medium text-sidebar-foreground"
            : "text-muted-foreground hover:bg-sidebar-active/60 hover:text-sidebar-foreground",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={cn("size-4 shrink-0", isActive && "text-primary")}
            aria-hidden="true"
          />
          {collapsed ? (
            <span className="sr-only">{label}</span>
          ) : (
            <span className="truncate">{label}</span>
          )}
        </>
      )}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}
